// src/components/ScanHistoryList.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchScanHistoryList, softDeleteScanHistory } from '../utils/api';
import { displayPrice, ScanHistory, ScanHistoryItem, PaginatedScanHistoryResponse } from '../utils/types';
import { toast } from 'react-toastify';
import Pagination from './Pagination';
import Spinner from './Spinner';
import { useCustomerContext } from '../context/customerContext'; // Import context
import { useStockLocationContext } from '../context/StockLocationContext';
import { FaTrash } from 'react-icons/fa';


const PRODUCTS_PER_PAGE_MODAL = 10;

const ScanHistoryList: React.FC = () => {

    const { t } = useTranslation();
    const { customers, loading: loadingCustomers } = useCustomerContext(); // Get customers from context

    const { locations } = useStockLocationContext();

    const [scanHistory, setScanHistory] = useState<ScanHistory[]>([]);
    const [loadingScanHistory, setLoadingScanHistory] = useState(true);
    const [errorScanHistory, setErrorScanHistory] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [filterMode, setFilterMode] = useState<'in' | 'out'>('in');

    const [scanHistoryId, setScanHistoryId] = useState<number | null>(null);


    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    const loadScanHistory = useCallback(async () => {
        setLoadingScanHistory(true);
        try {
            const data = await fetchScanHistoryList(currentPage);

            // Filter based on movement_type from filterMode
            const filtered = data.filter(
                (item: ScanHistory) =>
                    filterMode === 'in'
                        ? item.movement_type === 'stock_in'
                        : item.movement_type === 'stock_out'
            );
            // console.log(filtered);
            setScanHistory(filtered);
            setErrorScanHistory('');
        } catch (err: any) {
            setErrorScanHistory(err.message || 'Failed to load scan history.');
        } finally {
            setLoadingScanHistory(false);
        }
    }, [currentPage, filterMode]);


    useEffect(() => {
        loadScanHistory();
    }, [loadScanHistory]);

    if (errorScanHistory) return <div className="text-red-600">{errorScanHistory}</div>;

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this scan history record?')) {
            try {
                await softDeleteScanHistory(id);
                toast.success(`Scan history #${id} deleted.`);
                loadScanHistory(); // Refresh the list
            } catch (err: any) {
                toast.error(err.message || 'Failed to delete scan history.');
            }
        }
    };


    // Helper function to get username by user_id
    const getUsername = (userId: number) => {
        const user = customers.find(c => c.user_id === userId);
        if (!user) {
            return ``;
        } else {
            return user ? user.name : `${userId}`;
        }
    };

    // Helper function to get location name by location_id
    const getLocationName = (locationId: number | string) => {
        const id = Number(locationId);
        const location = locations.find((loc) => loc.id === id);
        return location ? location.name : 'N/A';
    };

    const headerTextColor: string = 'w-auto text-blue-600 block  item-start border-l border-l-1 border-b border-b-1 border-gray-400 p-1';

    // Calculate total items
    const totalItems = scanHistory.reduce(
        (sum, item) => sum + (Number(item.item_count) || 0),
        0
    );

    const totalRecordsMoreThanZero = scanHistory.filter(item => item.item_count > 0).length;

    return (
        <div className="overflow-x-auto mt-2">
            <div className='mt-1'>
                {/* <h2 className="text-xl font-semibold mb-4 text-center">Scan History</h2> */}

                <div className="flex justify-center gap-2 mb-1">
                    <button
                        onClick={() => {
                            if (filterMode === 'in') {
                                loadScanHistory();
                            } else {
                                setFilterMode('in');
                                setCurrentPage(1);
                            }
                        }}
                        className={`px-3 py-1 rounded text-sm font-medium border ${filterMode === 'in' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'
                            }`}
                    >
                        IN
                    </button>
                    <button
                        onClick={() => {
                            if (filterMode === 'out') {
                                loadScanHistory();
                            } else {
                                setFilterMode('out');
                                setCurrentPage(1);
                            }
                        }}
                        className={`px-3 py-1 rounded text-sm font-medium border ${filterMode === 'out' ? 'bg-amber-600 text-white' : 'bg-white text-amber-600 border-amber-600'
                            }`}
                    >
                        OUT
                    </button>
                </div>

                <div className="text-center text-gray-600 text-sm mt-2">
                    Total Items: <span className="font-bold">{totalItems}</span>
                    <br />
                    Total Records with items: <span className="font-bold">{totalRecordsMoreThanZero}</span>
                    <br />
                    Total Records: <span className="font-bold">{scanHistory.length} --&gt; ({scanHistory.length - totalRecordsMoreThanZero} records with 0 item)</span>

                </div>

            </div>

            <div className="text-xs md:text-sm mt-1 w-auto  mx-auto overflow-x-auto">
                {loadingScanHistory ? (
                    <Spinner />
                ) : (
                    <>
                        {totalPages > 1 && (
                            <div className="my-2 py-2 border-t"><Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></div>
                        )}

                        <div className="mt-1">

                            <div
                                className="bg-yellow-200 border border-gray-200 rounded shadow p-1"
                            >
                                <div className="mt-1">
                                    <div className="grid grid-cols-4 gap-0 md:grid-cols-8 font-bold ">
                                        <span className={`${headerTextColor} `}>SHID#</span>
                                        <span className={`${headerTextColor}`}>Date</span>
                                        <span className={`${headerTextColor}`}>Movement</span>
                                        <span className={`${headerTextColor}`}>Scanned By</span>
                                        <span className={`${headerTextColor}`}>Location</span>
                                        <span className={`${headerTextColor}`}>Status</span>
                                        {/* <span className={`${headerTextColor}`}>Confirmed By</span> */}
                                        <span className={`${headerTextColor}`}>Subject</span>
                                        <span className={`${headerTextColor}`}>Items Qty</span>

                                    </div>
                                </div>
                            </div>

                            <div className="text-gray-500 py-8">

                                {scanHistory.length > 0 ? (
                                    scanHistory.map(scan_history => {

                                        const statusTextColor: string = scan_history.status === 'pending' ?
                                            'w-auto text-gray-400  block  item-start border-l border-l-1 border-b border-b-1 border-gray-400 p-1' :
                                            'w-auto text-green-800  block item-start border-l border-l-1 border-b border-b-1 border-gray-400 p-1';

                                        return (
                                            scan_history.item_count > 0 ? (
                                                <div
                                                    key={scan_history.id}
                                                    className="bg-white border border-2 border-gray-300 rounded shadow p-1"
                                                >
                                                    <div className="mt-1">
                                                        <div className="flex items-center">
                                                            <div className="flex-grow">
                                                                <a href={`
                                                            ${window.APP_CONFIG?.PUBLIC_URL}/scan-history/${scan_history.movement_type}/${scan_history.id}
                                                            `}
                                                                    className={`  
                                                            grid grid-cols-4 gap-0 md:grid-cols-8 font-semibold hover:underline`} target='_blank' rel="noopener noreferrer">
                                                                    <span className={`${statusTextColor}`}>SHID#{scan_history.id}</span>
                                                                    <span className={`${statusTextColor}`}>{new Date(scan_history.created_at).toLocaleDateString('en-MY', { year: '2-digit', month: '2-digit', day: '2-digit' })}</span>
                                                                    <span className={`${statusTextColor}`}>{scan_history.movement_type}</span>
                                                                    {/* <span className={`${statusTextColor}`}>{scan_history.user_id}</span> */}
                                                                    <span className={`${statusTextColor} truncate hover:text-ellipsis`}>{getUsername(scan_history.user_id)}</span>
                                                                    <span className={`${statusTextColor}`}>{getLocationName(scan_history.stock_location_id)}</span>
                                                                    <span className={`${statusTextColor}`}>{scan_history.status}</span>
                                                                    {/* <span className={`${statusTextColor}`}>{getUsername(scan_history.confirmed_by) || 'N/A'}</span> */}
                                                                    <span className={`${statusTextColor} truncate hover:text-ellipsis`}>{scan_history.subject || 'N/A'}</span>
                                                                    <span className={`${statusTextColor}`}>{scan_history.item_count}</span>

                                                                </a>
                                                            </div>
                                                            {scan_history.item_count === 0 && (
                                                                <button onClick={() => handleDelete(scan_history.id)} className="ml-2 p-2 text-red-500 hover:text-red-700" title="Soft delete this record">
                                                                    <FaTrash />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null
                                        );

                                    })
                                ) : (
                                    <div className="text-center text-gray-500 py-8">
                                        No scan history items found.
                                    </div>
                                )}
                            </div>
                        </div>

                        {totalPages > 1 && (
                            <div className="my-2 py-2  border-t"><Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></div>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default ScanHistoryList;